import { relations, sql } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { user } from '../auth'
import { FunderTypeEnum } from './enums'
import { SeatEntitlement } from './seat-entitlement'

/**
 * A funder / sponsor of granted seats (e.g. Valbury Asia Venture, BFI Finance, a Pemda,
 * "PiBo" internal, or a paying school). Lightweight in-repo registry — not a CRM.
 */
export const Funder = pgTable(
  'funder',
  {
    id: uuid().primaryKey().defaultRandom(),
    name: text().notNull(),
    type: FunderTypeEnum().notNull(),
    contactEmail: text(),
    contactPhone: text(),
    notes: text(),
    isActive: boolean().notNull().default(true),
    createdBy: text().references(() => user.id, { onDelete: 'set null' }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [index('idx_funder_name').on(t.name)],
)

export const funderRelations = relations(Funder, ({ one, many }) => ({
  createdByUser: one(user, {
    fields: [Funder.createdBy],
    references: [user.id],
  }),

  entitlements: many(SeatEntitlement),
}))

export const InsertFunderSchema = createInsertSchema(Funder, {
  name: z.string().min(1),
  type: z.enum(FunderTypeEnum.enumValues),
  contactEmail: z.email().optional(),
  contactPhone: z.string().optional(),
  notes: z.string().optional(),
  isActive: z.boolean().default(true),
  createdBy: z.string().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateFunderSchema = createUpdateSchema(Funder, {
  name: z.string().min(1),
  type: z.enum(FunderTypeEnum.enumValues),
  contactEmail: z.email().nullable(),
  contactPhone: z.string().nullable(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
})
  .omit({
    id: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectFunderSchema = createSelectSchema(Funder, {
  id: z.uuid(),
  name: z.string(),
  type: z.enum(FunderTypeEnum.enumValues),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertFunder = typeof Funder.$inferInsert
export type UpdateFunder = z.infer<typeof UpdateFunderSchema>
export type SelectFunder = typeof Funder.$inferSelect
